import { AgencyUserDto } from "@api-types/user/AgencyUserDto";
import { AgencyUserFullUpdateDto } from "@api-types/user/AgencyUserFullUpdateDto";
import { DateTime } from "luxon";
import {
	PersonalDataResponseModel,
	UpdatePersonalDataRequestModel,
} from "src/data/types";
import { isNullOrWhitespace } from "@utils";

import { FieldValues } from "react-hook-form";
import { LanguagesLookupModel } from "@api-types";
import { LanguageDataType } from "@data-types/membership/registration";
import { SUPPORTED_LANGUAGES } from "@common/constants";

export const isPhoneValid = async (phone: string): Promise<boolean> => {
	if (phone == undefined) {
		return false;
	}
	if (isNullOrWhitespace(phone)) {
		return true;
	}

	try {
		const { PhoneNumberUtil } = await import("google-libphonenumber");
		const phoneUtil = PhoneNumberUtil.getInstance();
		return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
	} catch (error) {
		return false;
	}
};

export const removeUndefinedFields = (
	data: FieldValues,
	fetchedData: UpdatePersonalDataRequestModel
) => {
	const formData: FieldValues = {};
	let newObject = {} as UpdatePersonalDataRequestModel;

	Object.keys(data).map((fieldName) => {
		if (data[fieldName] !== undefined) {
			formData[fieldName] = data[fieldName];
		}
	});

	return Object.assign(newObject, fetchedData, formData);
};

export const mapDataForPasswordConfirmation = (
	data: FieldValues,
	dirtyFields: Partial<
		Readonly<{
			[x: string]: any;
		}>
	>,
	fetchedData: PersonalDataResponseModel
) => {
	const processedData = processDirtyFields(data, dirtyFields);

	const dataEntries = Object.entries(processedData);

	const mappedData = dataEntries.map((entry) => {
		//add icon for + and -
		if (entry[1] === true) {
			entry[1] = "+";
		} else if (entry[1] === false) {
			entry[1] = "-";
		} else if (entry[0] === "dob") {
			entry[1] = DateTime.fromISO(entry[1]).toLocaleString(
				DateTime.DATE_SHORT
			);
		} else if (entry[0] === "documentExpiration") {
			entry[1] = DateTime.fromISO(entry[1]).toLocaleString(
				DateTime.DATE_SHORT
			);
		} else if (entry[0] === "receiveNewsletter") {
			return {
				name: "specific:USER.ACCOUNT_SETTINGS.PERSONAL_DATA.NEWSLETTER_LABEL",
				value: entry[1],
			};
		}
		return {
			name: "personal-data:" + entry[0].toUpperCase(),
			value: entry[1],
		};
	});

	const getIDValues = changeIdToName(mappedData, fetchedData);
	return getIDValues;
};

export const mapLanguages = (
	defaultLangs: LanguagesLookupModel[] | undefined
) => {
	if (!defaultLangs) {
		return;
	}
	return defaultLangs.map((lang) => {
		return {
			id: lang.culture,
			name: lang.name,
		};
	});
};

export const filterLanguageList = (
	supportedLanguages: typeof SUPPORTED_LANGUAGES,
	allLanguages: LanguageDataType[]
) => {
	return allLanguages.filter((lookupLang) =>
		supportedLanguages.some(
			(supportedLang) =>
				lookupLang.iso === supportedLang.iso.toUpperCase()
		)
	);
};

export const mapDataForPersonalDataUpdate = (
	fetchedUserData: AgencyUserDto,
	dataModel: UpdatePersonalDataRequestModel
): AgencyUserFullUpdateDto => {
	const personalDataUpdateUserModel = new AgencyUserFullUpdateDto();

	personalDataUpdateUserModel.password = dataModel.password ?? "";
	personalDataUpdateUserModel.bankIdSessionId = dataModel.bankIdSessionId;
	personalDataUpdateUserModel.birthName = dataModel.birthName ?? "";
	personalDataUpdateUserModel.birthPlace = dataModel.birthPlace;
	personalDataUpdateUserModel.city = dataModel.city;
	personalDataUpdateUserModel.userName = fetchedUserData?.userName;
	personalDataUpdateUserModel.countryId = dataModel.countryId;
	personalDataUpdateUserModel.diagnosticLevel =
		//@ts-expect-error
		fetchedUserData?.diagnosticLevel;
	personalDataUpdateUserModel.dob = dataModel.dob;
	personalDataUpdateUserModel.documentExpiration =
		fetchedUserData.documentExpiration;
	personalDataUpdateUserModel.houseBox = fetchedUserData.houseBox;
	personalDataUpdateUserModel.houseNumber = dataModel.houseNumber;
	personalDataUpdateUserModel.isAgencyWideCashierEnabled =
		fetchedUserData?.isAgencyWideCashierEnabled;
	personalDataUpdateUserModel.isDiagnosticEnabled =
		//@ts-expect-error
		fetchedUserData?.isDiagnosticEnabled;
	personalDataUpdateUserModel.isLogTransferEnabled =
		//@ts-expect-error
		fetchedUserData?.isLogTransferEnabled;
	personalDataUpdateUserModel.language = dataModel.language;
	personalDataUpdateUserModel.mobilePhone = dataModel.mobilePhone;
	personalDataUpdateUserModel.nationality = dataModel?.nationality ?? "";
	personalDataUpdateUserModel.numericalId = fetchedUserData.numericalId;
	personalDataUpdateUserModel.occupationId = dataModel?.occupation ?? "";

	personalDataUpdateUserModel.passportId = fetchedUserData.passportId;
	personalDataUpdateUserModel.profession = dataModel.profession ?? "";
	personalDataUpdateUserModel.regionId = dataModel.regionId;
	personalDataUpdateUserModel.secondAddressReason =
		fetchedUserData.secondAddressReason;
	personalDataUpdateUserModel.secondCity = fetchedUserData.secondCity;
	personalDataUpdateUserModel.secondCountryId =
		fetchedUserData?.secondCountryId;
	personalDataUpdateUserModel.secondHouseNumber =
		fetchedUserData.secondHouseNumber;
	personalDataUpdateUserModel.secondStreet = fetchedUserData.secondStreet;
	personalDataUpdateUserModel.secondZIPCode = fetchedUserData.secondZipCode;
	personalDataUpdateUserModel.shopId = fetchedUserData.shopId;
	//@ts-expect-error
	personalDataUpdateUserModel.stakeAmount = fetchedUserData.stakeAmount;
	personalDataUpdateUserModel.street = dataModel.street;
	personalDataUpdateUserModel.telephoneNumber = dataModel.telephoneNumber;
	personalDataUpdateUserModel.titleId = dataModel.titleId;
	personalDataUpdateUserModel.userAccountRelationMatchStatus =
		//@ts-expect-error
		fetchedUserData.userAccountRelationMatchStatus;
	personalDataUpdateUserModel.zipCode = dataModel.zipCode;
	personalDataUpdateUserModel.firstName = dataModel.firstName;
	personalDataUpdateUserModel.lastName = dataModel.lastName;
	personalDataUpdateUserModel.creationDate =
		//@ts-expect-error
		fetchedUserData?.creationDate;
	personalDataUpdateUserModel.cultureInfoName = dataModel.language;
	//@ts-expect-error
	personalDataUpdateUserModel.displayName = fetchedUserData?.displayName;
	personalDataUpdateUserModel.email = dataModel.currentEmail;
	personalDataUpdateUserModel.isApproved = fetchedUserData.isApproved;
	personalDataUpdateUserModel.isLockedOut = fetchedUserData.isLockedOut;
	personalDataUpdateUserModel.lastActivityDate =
		//@ts-expect-error
		fetchedUserData?.lastActivityDate;
	personalDataUpdateUserModel.lastLockoutDate =
		//@ts-expect-error
		fetchedUserData?.lastLockoutDate;
	//@ts-expect-error
	personalDataUpdateUserModel.lastLoginDate = fetchedUserData?.lastLoginDate;
	//@ts-expect-error
	personalDataUpdateUserModel.name = fetchedUserData?.name;
	//@ts-expect-error
	personalDataUpdateUserModel.roles = fetchedUserData.roles;
	//@ts-expect-error
	personalDataUpdateUserModel.timeZoneId = fetchedUserData.timeZoneId;
	personalDataUpdateUserModel.json = fetchedUserData?.json;
	personalDataUpdateUserModel.personalIdentificator =
		dataModel?.personalIdentificator ?? "";

	personalDataUpdateUserModel.documentExpiration =
		dataModel.documentExpiration;
	if (dataModel?.annualIncome) {
		personalDataUpdateUserModel.annualIncomeSpanId =
			dataModel?.annualIncome;
	}

	return personalDataUpdateUserModel;
};

const processDirtyFields = (
	data: FieldValues,
	dirtyFields: Partial<
		Readonly<{
			[x: string]: any;
		}>
	>
) => {
	const touchedFields: Partial<{
		[x: string]: any;
	}> = {};
	Object.keys(dirtyFields).map((fieldName) => {
		touchedFields[fieldName] = data[fieldName];
	});
	return touchedFields;
};

const changeIdToName = (
	data: { name: string; value: string }[],
	fetchedData: PersonalDataResponseModel
) => {
	const changedData = data.map((x) => {
		const isNationality = x.name.includes("NATIONALITY");

		let getName;

		if (
			fetchedData.languages &&
			fetchedData.userTitles &&
			fetchedData.countries &&
			fetchedData.countryRegions &&
			fetchedData.occupationData
		) {
			getName =
				fetchedData.languages.find(
					(lang) => lang.culture === x.value
				) ||
				fetchedData.userTitles.find((title) => title.id === x.value) ||
				fetchedData.countries.find(
					(country) => country.id === x.value
				) ||
				fetchedData.countryRegions.find(
					(state) => state.id === x.value
				) ||
				fetchedData.occupationData.find(
					(occupation) => occupation.id === x.value
				);
		}

		if (getName && getName.name) {
			x.value = getName.name;
			if (isNationality) {
				//@ts-expect-error
				x.value = getName.nationality;
			}
		} else if (fetchedData.annualIncomeSpans) {
			const spanItem = fetchedData.annualIncomeSpans.find(
				(span) => span.id === x.value
			);

			if (spanItem) {
				x.value = spanItem.span;
			}
		}

		return x;
	});
	return changedData;
};
